import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/cn';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ className, label, indeterminate, id, checked, ...rest }, ref) {
    const cid = id || `c-${Math.random().toString(36).slice(2, 8)}`;
    const innerRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = !!indeterminate;
    }, [indeterminate]);

    return (
      <label
        htmlFor={cid}
        className={cn(
          'inline-flex items-center gap-2 cursor-pointer select-none',
          rest.disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <span className="relative inline-flex w-4 h-4">
          <input
            id={cid}
            ref={(el) => {
              innerRef.current = el;
              if (typeof ref === 'function') ref(el);
              else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }}
            type="checkbox"
            checked={checked}
            className="absolute inset-0 opacity-0 cursor-pointer"
            {...rest}
          />
          <span
            aria-hidden="true"
            className={cn(
              'w-4 h-4 rounded border bg-white border-slate-300 transition-colors',
              'flex items-center justify-center text-white',
              (checked || indeterminate) && 'bg-brand-600 border-brand-600',
              className
            )}
          >
            {indeterminate ? (
              <Minus className="w-3 h-3" />
            ) : checked ? (
              <Check className="w-3 h-3" />
            ) : null}
          </span>
        </span>
        {label && <span className="text-sm text-slate-700">{label}</span>}
      </label>
    );
  }
);
